// frontend/src/components/negocios/NegociosFiltroBar.jsx
import React from 'react';
import { Filter, Search, X } from 'lucide-react';
import { TIPOS_IMOVEL } from '../../constants/negocios';

/**
 * ✅ Central de Negócios — Filtros
 * - Rede: Corretor (select), Tipo, Valor, Bairro, Permuta, Pesquisa
 * - Meus: Tipo, Valor, Bairro, Permuta, Pesquisa (sem corretor)
 *
 * Props:
 * - filtros, onChange
 * - listaBairros
 *
 * 🆕 Rede
 * - showCorretorSelect: boolean
 * - corretoresSelect: [{ id, nome, count }]
 */
export default function NegociosFiltroBar({
  filtros,
  onChange,
  listaBairros = [],

  // 🆕 filtro corretor (select)
  showCorretorSelect = false,
  corretoresSelect = [],
}) {
  const handleChange = (campo) => (e) => {
    const valor = e.target.value;
    onChange((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const limpar = () => {
    onChange((prev) => ({
      ...prev,
      tipoImovel: '',
      faixaValor: '',
      bairro: '',
      pesquisa: '',
      permuta: '',
      corretorId: 'all',
    }));
  };

  return (
    <div className="mt-3 rounded-2xl bg-white border border-gray-100 px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Filter className="w-4 h-4" />
          Filtros
        </div>

        <button
          type="button"
          onClick={limpar}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <X className="w-3 h-3" />
          Limpar
        </button>
      </div>

      {/* Linha única */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
        {/* Selects */}
        <div className="flex flex-wrap md:flex-nowrap flex-none gap-2">
          {/* Corretor (Rede) */}
          {showCorretorSelect && (
            <div className="relative">
              <select
                value={filtros.corretorId || 'all'}
                onChange={handleChange('corretorId')}
                className="h-10 min-w-[190px] appearance-none rounded-xl border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">Todos os corretores</option>
                {corretoresSelect.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {typeof c.count === 'number' ? ` (${c.count})` : ''}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                ▼
              </span>
            </div>
          )}

          {/* ✅ Tipo de imóvel (IGUAL ao Cadastrar Negócio: TIPOS_IMOVEL) */}
          <div className="relative">
            <select
              value={filtros.tipoImovel || ''}
              onChange={handleChange('tipoImovel')}
              className="h-10 min-w-[180px] appearance-none rounded-xl border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Tipo de imóvel</option>
              {(TIPOS_IMOVEL || []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              ▼
            </span>
          </div>

          {/* Valor */}
          <div className="relative">
            <select
              value={filtros.faixaValor || ''}
              onChange={handleChange('faixaValor')}
              className="h-10 min-w-[190px] appearance-none rounded-xl border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Valor de interesse</option>
              <option value="ate200">Até R$ 200 mil</option>
              <option value="200a400">R$ 200 mil – R$ 400 mil</option>
              <option value="400a600">R$ 400 mil – R$ 600 mil</option>
              <option value="600a800">R$ 600 mil – R$ 800 mil</option>
              <option value="800a1m">R$ 800 mil – R$ 1 milhão</option>
              <option value="acima1m">Acima de R$ 1 milhão</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              ▼
            </span>
          </div>

          {/* Bairro */}
          <div className="relative">
            <select
              value={filtros.bairro || ''}
              onChange={handleChange('bairro')}
              className="h-10 min-w-[160px] appearance-none rounded-xl border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Bairros</option>
              {listaBairros.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              ▼
            </span>
          </div>

          {/* Permuta */}
          <div className="relative">
            <select
              value={filtros.permuta || ''}
              onChange={handleChange('permuta')}
              className="h-10 min-w-[150px] appearance-none rounded-xl border border-gray-200 bg-white px-3 pr-9 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Permuta</option>
              <option value="sim">Aceita</option>
              <option value="nao">Não aceita</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              ▼
            </span>
          </div>
        </div>

        {/* Pesquisa */}
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={filtros.pesquisa || ''}
              onChange={handleChange('pesquisa')}
              placeholder="Pesquisar por tipo, bairro, cidade, descrição..."
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-3 pr-10 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
